import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    CircularProgress,
    TextField,
    IconButton,
    Typography,
    Box,
    Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DateShow from '../../date-filed/DateShow';
import NeckSizeGraph from './Ghraph';
import { get, post, patch, remove } from '../../../services/api-services';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { StyledButton, Textarea } from '../../StyledComponent';
import { weightRegex } from '../../../regex/regex';
import { getCurrentDateTime } from '../../../modules/ConvertDate';
import { useParams } from 'react-router-dom';
import { useCommon } from '../../../context/CommonContext';
import DeleteDialog from '../../../dialogs/DeleteDialog';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';

export default function AddMeasurementDialog({ fetchUserData, userData, open, onClose, type, measurementData, setMeasurementData, fetchData, GraphfetchData, formatTypeMessage }) {
    const [measurements, setMeasurements] = useState([]);
    const [loading, setLoading] = useState(true);
    const { setSnackbar } = useCommon();
    const today = new Date();
    const maxDate = today.toISOString().slice(0, 16);
    const [editingId, setEditingId] = useState(null);
    const [openDelete, setOpenDelete] = useState(false);
    const [buttonLoading, setButtonLoading] = useState(false);

    const { id } = useParams();
    useEffect(() => {
        if (type) {
            setMeasurementData((prevData) => ({
                ...prevData,
                type: type,
            }));
        }
    }, [type, setMeasurementData]);

    const AddfetchData = useCallback(async () => {
        const url = `/Measurements?filter={"where": {"customerId": "${id}", "type": "${type}"},"order":"recordedAt DESC","limit":30}`;
        try {
            const response = await get(url);
            if (response && response.data) {
                console.log('Measurements from API:', response.data);
                setMeasurements(response.data);
            }

        } catch (error) {
            console.error('Error fetching measurements:', error);
        } finally {
            setLoading(false);
        }
    }, [id, type]);

    useEffect(() => {
        if (open) {
            AddfetchData();
        }
    }, [open, AddfetchData]);



    const handleAddMeasurement = async () => {
        setButtonLoading(true);

        try {
            const recordedDate = new Date(measurementData.recordedAt);
            const payload = {
                ...measurementData,
                recordedAt: !isNaN(recordedDate.getTime()) ? recordedDate.toISOString() : new Date().toISOString()
            };
            const res = await post(`/Measurements`, payload);

            if (res?.statusCode === 200) {
                setSnackbar({
                    openSnackbar: true,
                    message: `${formatTypeMessage(measurementData?.type)} added successfully!`,
                    severity: 'success'
                });

                if (measurementData?.type === "height") {
                    const newHeight = measurementData?.value || null;
                    const height = newHeight;
                    if (height) {
                        await patch(`/Customers/${userData.id}`, { height: height });
                        console.log("Height exists in userData:", height);
                    } else {
                        await post(`/Customers/${userData.id}`, { ...userData, height });
                        console.log("Height is missing in userData!");
                    }
                    fetchUserData()
                }
                // Fetch latest measurements and update UI
                AddfetchData();
                fetchData();
                await GraphfetchData(measurementData?.type);
                setMeasurementData((prev) => ({
                    ...prev,
                    value: '',
                    userComment: ''
                }));
                // onClose();
            }
        } catch (error) {
            console.error("Error adding measurement:", error);
        } finally {
            setButtonLoading(false);
        }
    };


    const handleEditClick = (measurement) => {
        setEditingId(measurement.id);
        setMeasurementData({
            type: measurement.type,
            value: measurement.value,
            unit: measurement.unit || '',
            recordedAt: measurement.recordedAt,
            userComment: measurement.userComment || '',
        });
    };

    const handleSaveEdit = async () => {
        const recordedDate = new Date(measurementData.recordedAt);
        const payload = {
            value: measurementData.value,
            unit: measurementData.unit,
            recordedAt: !isNaN(recordedDate.getTime()) ? recordedDate.toISOString() : new Date().toISOString(),
            userComment: measurementData.userComment || '',
        };
        const res = await patch(`/Measurements/${editingId}`, payload);
        if (res?.statusCode === 200) {
            setSnackbar({
                openSnackbar: true,
                message: `${formatTypeMessage(type)} updated successfully!`,
                severity: 'success'
            });
            if (measurementData?.type === "height") {
                const newHeight = measurementData?.value || null;
                if (newHeight) {
                    await patch(`/Customers/${userData.id}`, { height: newHeight });
                    console.log("Height updated successfully:", newHeight);
                }
                fetchUserData()
            }

            AddfetchData();
            setEditingId(null);
            fetchData();
            onClose()
        }
    };

    const onDeletemeasurement = async () => {
        const res = await remove(`/Measurements/${editingId}`);
        if (res?.statusCode === 200) {
            setSnackbar({
                openSnackbar: true,
                message: `${type} deleted successfully!`,
                severity: 'success'
            });
            setOpenDelete(false);
            setEditingId(null);
            AddfetchData();
        }
    };
    return (
        <Dialog
            fullWidth
            open={open}
            onClose={onClose}
            aria-labelledby="measurement-dialog-title"
        >
            <DialogTitle id="measurement-dialog-title" fontWeight='bold'>Add {formatTypeMessage(type)} </DialogTitle>
            <IconButton
                size='small'
                aria-label="close"
                onClick={onClose}
                sx={(theme) => ({
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500],
                })}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent>
                {/* Graph Section */}
                <Box marginBottom={6}>
                    <NeckSizeGraph type={type} showLine={true} measurements={measurements} />
                </Box>
                <Divider />

                {/* Add Measurement Section */}
                {!editingId &&
                    <Box marginTop={2}>
                        <Box display='flex' gap={2}>
                            <DateTimePicker
                                label="Choose date and time"
                                value={measurementData.recordedAt ? dayjs(measurementData.recordedAt) : null}
                                onChange={(newValue) => {
                                    setMeasurementData({ ...measurementData, recordedAt: newValue ? newValue.toISOString() : null });
                                }}
                                maxDateTime={dayjs()}
                                slotProps={{
                                    textField: {
                                        autoFocus: true,
                                        required: true,
                                        margin: "dense",
                                        id: "recordedAt",
                                        name: "recordedAt",
                                        fullWidth: true,
                                        variant: "standard"
                                    }
                                }}
                            />
                            {measurementData?.type === 'weight' ?
                                <TextField
                                    autoFocus
                                    required
                                    margin="dense"
                                    id="weight"
                                    name="weight"
                                    label="Weight (In kgs)"
                                    type="number"
                                    fullWidth
                                    variant="standard"
                                    value={measurementData.value}
                                    onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                    onInput={(e) => {
                                        if (e.target.value.length > 5) {
                                            e.target.value = e.target.value.slice(0, 5);
                                        }
                                    }}
                                    error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 300 || measurementData.value <= 20)))}
                                    helperText={(measurementData.value && (measurementData.value >= 300 || measurementData.value <= 20)) ? "Weight should be between 20 to 300 kgs" : ''}
                                />
                                : measurementData?.type === 'chestSize' ?
                                    <TextField
                                        autoFocus
                                        required
                                        margin="dense"
                                        id="chestSize"
                                        name="chestSize"
                                        label="Chest Size(In Cms)"
                                        type="number"
                                        fullWidth
                                        variant="standard"
                                        value={measurementData.value}
                                        onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                        onInput={(e) => {
                                            if (e.target.value.length > 5) {
                                                e.target.value = e.target.value.slice(0, 5);
                                            }
                                        }}
                                        error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 300 || measurementData.value <= 25)))}
                                        helperText={(measurementData.value && (measurementData.value >= 300 || measurementData.value <= 25)) ? "Chest Size must be in between 25 to 300" : ''}
                                    />
                                    : measurementData?.type === 'waistSize' ?
                                        <TextField
                                            autoFocus
                                            required
                                            margin="dense"
                                            id="waistSize"
                                            name="waistSize"
                                            label="Waist Size(In Cms)"
                                            type="number"
                                            fullWidth
                                            variant="standard"
                                            value={measurementData.value}
                                            onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                            onInput={(e) => {
                                                if (e.target.value.length > 5) {
                                                    e.target.value = e.target.value.slice(0, 5);
                                                }
                                            }}
                                            error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 300 || measurementData.value <= 25)))}
                                            helperText={(measurementData.value && (measurementData.value >= 300 || measurementData.value <= 25)) ? "Waist Size must be in between 25 to 300" : ''}
                                        /> : measurementData?.type === 'hipsSize' ?
                                            <TextField
                                                autoFocus
                                                required
                                                margin="dense"
                                                id="hipsSize"
                                                name="hipsSize"
                                                label="Hips Size (In Cms)"
                                                type="number"
                                                fullWidth
                                                variant="standard"
                                                value={measurementData.value}
                                                onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                onInput={(e) => {
                                                    if (e.target.value.length > 5) {
                                                        e.target.value = e.target.value.slice(0, 5);
                                                    }
                                                }}
                                                error={
                                                    measurementData.value &&
                                                    (measurementData.value < 25 || measurementData.value > 300 || !weightRegex.test(measurementData.value))
                                                }
                                                helperText={
                                                    measurementData.value &&
                                                        (measurementData.value < 25 || measurementData.value > 300)
                                                        ? "Hips Size must be between 25 and 300"
                                                        : ''
                                                }
                                            />

                                            : measurementData?.type === 'thighsSize' ?
                                                <TextField
                                                    autoFocus
                                                    required
                                                    margin="dense"
                                                    id="thighsSize"
                                                    name="thighsSize"
                                                    label="Thighs Size(In Cms)"
                                                    type="number"
                                                    fullWidth
                                                    variant="standard"
                                                    value={measurementData.value}
                                                    onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                    onInput={(e) => {
                                                        if (e.target.value.length > 5) {
                                                            e.target.value = e.target.value.slice(0, 5);
                                                        }
                                                    }}
                                                    error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 15 || measurementData.value <= 300)))}
                                                    helperText={(measurementData.value && (measurementData.value >= 300 || measurementData.value <= 15)) ? "Thighs Size must be in between 15 to 300" : ''}
                                                /> : measurementData?.type === 'armsSize' ?
                                                    <TextField
                                                        autoFocus
                                                        required
                                                        margin="dense"
                                                        id="armsSize"
                                                        name="armsSize"
                                                        label="Arms Size(In Cms)"
                                                        type="number"
                                                        fullWidth
                                                        variant="standard"
                                                        value={measurementData.value}
                                                        onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                        onInput={(e) => {
                                                            if (e.target.value.length > 5) {
                                                                e.target.value = e.target.value.slice(0, 5);
                                                            }
                                                        }}
                                                        error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 300 || measurementData.value <= 5)))}
                                                        helperText={(measurementData.value && (measurementData.value >= 300 || measurementData.value <= 5)) ? "Arms Size must be in between 5 to 300" : ''}
                                                    />
                                                    : measurementData?.type === 'overallBodyFat' ?
                                                        <TextField
                                                            autoFocus
                                                            required
                                                            margin="dense"
                                                            id="overallBodyFat"
                                                            name="overallBodyFat"
                                                            label="Overall Body Fat Size(In %)"
                                                            type="number"
                                                            fullWidth
                                                            variant="standard"
                                                            value={measurementData.value}
                                                            onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                            onInput={(e) => {
                                                                if (e.target.value.length > 5) {
                                                                    e.target.value = e.target.value.slice(0, 5);
                                                                }
                                                            }}
                                                            error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 100 || measurementData.value <= 0)))}
                                                            helperText={(measurementData.value && (measurementData.value >= 100 || measurementData.value <= 0)) ? "Overall Body Fat must be in between 0 to 100" : ''}
                                                        /> : measurementData?.type === 'skeletalMuscleFat' ?
                                                            <TextField
                                                                autoFocus
                                                                required
                                                                margin="dense"
                                                                id="skeletalMuscleFat"
                                                                name="skeletalMuscleFat"
                                                                label="Skeletal Muscle Fat Size(In %)"
                                                                type="number"
                                                                fullWidth
                                                                variant="standard"
                                                                value={measurementData.value}
                                                                onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                                onInput={(e) => {
                                                                    if (e.target.value.length > 5) {
                                                                        e.target.value = e.target.value.slice(0, 5);
                                                                    }
                                                                }}
                                                                error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 100 || measurementData.value <= 0)))}
                                                                helperText={(measurementData.value && (measurementData.value >= 100 || measurementData.value <= 0)) ? "Skeletal Muscle must be in between 0 to 100" : ''}
                                                            /> : measurementData?.type === 'visceralFat' ?
                                                                <TextField
                                                                    autoFocus
                                                                    required
                                                                    margin="dense"
                                                                    id="visceralFat"
                                                                    name="visceralFat"
                                                                    label="Visceral Fat Size(In %)"
                                                                    type="number"
                                                                    fullWidth
                                                                    variant="standard"
                                                                    value={measurementData.value}
                                                                    onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                                    onInput={(e) => {
                                                                        if (e.target.value.length > 5) {
                                                                            e.target.value = e.target.value.slice(0, 5);
                                                                        }
                                                                    }}
                                                                    error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 100 || measurementData.value <= 0)))}
                                                                    helperText={(measurementData.value && (measurementData.value >= 100 || measurementData.value <= 0)) ? "Visceral Fat must be in between 0 to 100" : ''}
                                                                /> : measurementData?.type === 'height' ?
                                                                    <TextField
                                                                        autoFocus
                                                                        required
                                                                        margin="dense"
                                                                        id="height"
                                                                        name="height"
                                                                        label="Height(In Cms)"
                                                                        type="number"
                                                                        fullWidth
                                                                        variant="standard"
                                                                        value={measurementData.value}
                                                                        onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                                        onInput={(e) => {
                                                                            if (e.target.value.length > 5) {
                                                                                e.target.value = e.target.value.slice(0, 5);
                                                                            }
                                                                        }}
                                                                        error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 300 || measurementData.value <= 120)))}
                                                                        helperText={(measurementData.value && (measurementData.value >= 300 || measurementData.value <= 120)) ? "Height must be in between 120 to 300" : ''}
                                                                    /> : measurementData?.type === 'subcutaneousFat' ?
                                                                        <TextField
                                                                            autoFocus
                                                                            required
                                                                            margin="dense"
                                                                            id="subcutaneousFat"
                                                                            name="subcutaneousFat"
                                                                            label="Subcutaneous Fat(In %)"
                                                                            type="number"
                                                                            fullWidth
                                                                            variant="standard"
                                                                            value={measurementData.value}
                                                                            onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                                            onInput={(e) => {
                                                                                if (e.target.value.length > 5) {
                                                                                    e.target.value = e.target.value.slice(0, 5);
                                                                                }
                                                                            }}
                                                                            error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 100 || measurementData.value <= 0)))}
                                                                            helperText={(measurementData.value && (measurementData.value >= 100 || measurementData.value <= 0)) ? "Subcutaneous Fat must be in between 0 to 100" : ''}
                                                                        /> : measurementData?.type === 'bmr' ?
                                                                            <TextField
                                                                                autoFocus
                                                                                required
                                                                                margin="dense"
                                                                                id="bmr"
                                                                                name="bmr"
                                                                                label="Basal Metabolic Rate (BMR)(In Cal/day)"
                                                                                type="number"
                                                                                fullWidth
                                                                                variant="standard"
                                                                                value={measurementData.value}
                                                                                onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                                                onInput={(e) => {
                                                                                    if (e.target.value.length > 5) {
                                                                                        e.target.value = e.target.value.slice(0, 5);
                                                                                    }
                                                                                }}
                                                                                error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 5000 || measurementData.value <= 500)))}
                                                                                helperText={(measurementData.value && (measurementData.value >= 5000 || measurementData.value <= 500)) ? "Basal Metabolic Rate (BMR) must be in between 500 to 5000" : ''}
                                                                            /> : measurementData?.type === 'neck' ?
                                                                                <TextField
                                                                                    autoFocus
                                                                                    required
                                                                                    margin="dense"
                                                                                    id="neck"
                                                                                    name="neck"
                                                                                    label="Neck Size(In Cms)"
                                                                                    type="number"
                                                                                    fullWidth
                                                                                    variant="standard"
                                                                                    value={measurementData.value}
                                                                                    onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                                                    onInput={(e) => {
                                                                                        if (e.target.value.length > 5) {
                                                                                            e.target.value = e.target.value.slice(0, 5);
                                                                                        }
                                                                                    }}
                                                                                    error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 300 || measurementData.value <= 5)))}
                                                                                    helperText={(measurementData.value && (measurementData.value >= 300 || measurementData.value <= 5)) ? "Neck Size must be in between 5 to 300" : ''}
                                                                                /> : ''
                            }

                        </Box>

                        <Textarea
                            rows="4"
                            placeholder="Add a comment... (optional)"
                            value={measurementData?.userComment}
                            className="w-full p-2 rounded"
                            onChange={(e) =>
                                setMeasurementData({ ...measurementData, userComment: e.target.value })
                            }
                        />
                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                            width="100%"
                            sx={{ marginY: 2 }}
                        >
                            {/* <StyledButton
                                variant="contained"
                                color="primary"
                                onClick={handleAddMeasurement}
                                //disabled={!measurementData?.value && !measurementData?.recordedAt ? true : false}
                                disabled={
                                    !measurementData?.value || 
                                    (measurementData?.type === 'weight' && (measurementData.value < 20 || measurementData.value > 220)) || 
                                    (measurementData?.type === 'neck' && (measurementData.value < 5 || measurementData.value > 30)) || // Weight validation
                                    (measurementData?.type === 'chestSize' && (measurementData.value < 25 || measurementData.value > 100)) ||  // Chest size validation
                                    (measurementData?.type === 'waistSize' && (measurementData.value < 25 || measurementData.value > 150)) ||  // Waist size validation
                                    (measurementData?.type === 'hipsSize' && (measurementData.value < 25 || measurementData.value > 150)) ||  // Hips size validation
                                    (measurementData?.type === 'thighsSize' && (measurementData.value < 15 || measurementData.value > 50)) ||
                                    (measurementData?.type === 'armsSize' && (measurementData.value < 5 || measurementData.value > 50)) ||
                                    (measurementData?.type === 'overallBodyFat' && (measurementData.value < 3 || measurementData.value > 50)) ||
                                    (measurementData?.type === 'skeletalMuscleFat' && (measurementData.value < 3 || measurementData.value > 50)) ||
                                    (measurementData?.type === 'visceralFat' && (measurementData.value < 3 || measurementData.value > 50))||
                                    (measurementData?.type === 'subcutaneousFat' && (measurementData.value < 12 || measurementData.value > 50)) ||
                                    (measurementData?.type === 'bmr' && (measurementData.value < 500 || measurementData.value > 4000)) ||
                                    (measurementData?.type === 'height' && (measurementData.value < 120 || measurementData.value > 240))
                                }
                            >
                                ADD
                            </StyledButton> */}
                            <StyledButton
                                variant="contained"
                                color="primary"
                                onClick={handleAddMeasurement}
                                disabled={buttonLoading ||
                                    !measurementData?.value ||
                                    (measurementData?.type === 'weight' && (measurementData.value < 20 || measurementData.value > 300)) ||
                                    (measurementData?.type === 'neck' && (measurementData.value < 5 || measurementData.value > 300)) ||
                                    (measurementData?.type === 'chestSize' && (measurementData.value < 25 || measurementData.value > 300)) ||
                                    (measurementData?.type === 'waistSize' && (measurementData.value < 25 || measurementData.value > 300)) ||
                                    (measurementData?.type === 'hipsSize' && (measurementData.value < 25 || measurementData.value > 300)) ||
                                    (measurementData?.type === 'thighsSize' && (measurementData.value < 15 || measurementData.value > 300)) ||
                                    (measurementData?.type === 'armsSize' && (measurementData.value < 5 || measurementData.value > 300)) ||
                                    (measurementData?.type === 'overallBodyFat' && (measurementData.value < 0 || measurementData.value > 100)) ||
                                    (measurementData?.type === 'skeletalMuscleFat' && (measurementData.value < 0 || measurementData.value > 100)) ||
                                    (measurementData?.type === 'visceralFat' && (measurementData.value < 0 || measurementData.value > 100)) ||
                                    (measurementData?.type === 'subcutaneousFat' && (measurementData.value < 0 || measurementData.value > 100)) ||
                                    (measurementData?.type === 'bmr' && (measurementData.value < 500 || measurementData.value > 5000)) ||
                                    (measurementData?.type === 'height' && (measurementData.value < 120 || measurementData.value > 300))
                                }
                            >
                                {buttonLoading ? "Adding..." : "ADD"}
                            </StyledButton>

                        </Box>
                    </Box>
                }



                {/* Measurements List */}
                {loading ? (
                    <Box textAlign="center" padding="20px">
                        <CircularProgress />
                    </Box>
                ) : (
                    measurements.map((measurement) => (
                        <Box
                            key={measurement.id}
                            display="flex"
                            flexDirection='column'
                            justifyContent="space-between"
                            // alignItems="center"
                            padding="8px 0"
                            borderBottom="1px solid #f0f0f0"
                        >
                            <Typography variant="body2" display="flex" alignItems="center" justifyContent="space-between">
                                <DateShow date={measurement.recordedAt} />
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="body2">
                                        {measurement.value} {measurement.unit}
                                    </Typography>
                                    <IconButton size='small' onClick={() => handleEditClick(measurement)}>
                                        <EditIcon color="primary" />
                                    </IconButton>
                                </Box>
                            </Typography>
                            {/* Comment display with fallback keys */}
                            {(measurement?.userComment || measurement?.comments || measurement?.comment || measurement?.consumerComment || measurement?.adminComment) && (
                                <Box sx={{ mt: 1, mb: 1, width: '100%', borderLeft: '3px solid #eee', pl: 1 }}>
                                    <Textarea
                                        rows="2"
                                        readOnly
                                        value={measurement.userComment || measurement.comments || measurement.comment || measurement.consumerComment || measurement.adminComment}
                                        style={{
                                            fontSize: '13px',
                                            fontStyle: 'italic',
                                            opacity: 0.9,
                                            backgroundColor: 'transparent',
                                            border: 'none',
                                            padding: '4px',
                                            resize: 'none'
                                        }}
                                    />
                                </Box>
                            )}


                            {/* Conditionally render edit form below the selected row */}
                            {editingId === measurement.id && (
                                <Box padding="16px" marginTop={2} border="1px solid #f0f0f0" borderRadius="8px">
                                    <Box container spacing={2} alignItems="center">
                                        <Box item display='flex' gap={2}>
                                            <DateTimePicker
                                                label="Choose date and time"
                                                value={measurementData.recordedAt ? dayjs(measurementData.recordedAt) : null}
                                                onChange={(newValue) => {
                                                    setMeasurementData({ ...measurementData, recordedAt: newValue ? newValue.toISOString() : null });
                                                }}
                                                maxDateTime={dayjs()}
                                                slotProps={{
                                                    textField: {
                                                        autoFocus: true,
                                                        required: true,
                                                        margin: "dense",
                                                        id: "recordedAt",
                                                        name: "recordedAt",
                                                        fullWidth: true,
                                                        variant: "standard"
                                                    }
                                                }}
                                            />
                                            {measurementData?.type === 'weight' ?
                                                <TextField
                                                    autoFocus
                                                    required
                                                    margin="dense"
                                                    id="weight"
                                                    name="weight"
                                                    label="Weight (In kgs)"
                                                    type="number"
                                                    fullWidth
                                                    variant="standard"
                                                    value={measurementData.value}
                                                    onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                    onInput={(e) => {
                                                        if (e.target.value.length > 5) {
                                                            e.target.value = e.target.value.slice(0, 5);
                                                        }
                                                    }}
                                                    error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 300 || measurementData.value <= 20)))}
                                                    helperText={(measurementData.value && (measurementData.value >= 300 || measurementData.value <= 20)) ? "Weight should be between 20 to 300 kgs" : ''}
                                                />
                                                : measurementData?.type === 'chestSize' ?
                                                    <TextField
                                                        autoFocus
                                                        required
                                                        margin="dense"
                                                        id="chestSize"
                                                        name="chestSize"
                                                        label="Chest Size(In Cms)"
                                                        type="number"
                                                        fullWidth
                                                        variant="standard"
                                                        value={measurementData.value}
                                                        onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                        onInput={(e) => {
                                                            if (e.target.value.length > 5) {
                                                                e.target.value = e.target.value.slice(0, 5);
                                                            }
                                                        }}
                                                        error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 300 || measurementData.value <= 25)))}
                                                        helperText={(measurementData.value && (measurementData.value >= 300 || measurementData.value <= 25)) ? "Chest Size must be in between 25 to 300" : ''}
                                                    />
                                                    : measurementData?.type === 'waistSize' ?
                                                        <TextField
                                                            autoFocus
                                                            required
                                                            margin="dense"
                                                            id="waistSize"
                                                            name="waistSize"
                                                            label="Waist Size(In Cms)"
                                                            type="number"
                                                            fullWidth
                                                            variant="standard"
                                                            value={measurementData.value}
                                                            onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                            onInput={(e) => {
                                                                if (e.target.value.length > 5) {
                                                                    e.target.value = e.target.value.slice(0, 5);
                                                                }
                                                            }}
                                                            error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 300 || measurementData.value <= 25)))}
                                                            helperText={(measurementData.value && (measurementData.value >= 300 || measurementData.value <= 25)) ? "Waist Size must be in between 25 to 300" : ''}
                                                        /> : measurementData?.type === 'hipsSize' ?
                                                            <TextField
                                                                autoFocus
                                                                required
                                                                margin="dense"
                                                                id="hipsSize"
                                                                name="hipsSize"
                                                                label="Hips Size(In Cms)"
                                                                type="number"
                                                                fullWidth
                                                                variant="standard"
                                                                value={measurementData.value}
                                                                onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                                onInput={(e) => {
                                                                    if (e.target.value.length > 5) {
                                                                        e.target.value = e.target.value.slice(0, 5);
                                                                    }
                                                                }}
                                                                error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 25 || measurementData.value <= 300)))}
                                                                helperText={(measurementData.value && (measurementData.value >= 300 || measurementData.value <= 25)) ? "Hips Size must be in between 25 to 300" : ''}
                                                            /> : measurementData?.type === 'thighsSize' ?
                                                                <TextField
                                                                    autoFocus
                                                                    required
                                                                    margin="dense"
                                                                    id="thighsSize"
                                                                    name="thighsSize"
                                                                    label="Thighs Size(In Cms)"
                                                                    type="number"
                                                                    fullWidth
                                                                    variant="standard"
                                                                    value={measurementData.value}
                                                                    onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                                    onInput={(e) => {
                                                                        if (e.target.value.length > 5) {
                                                                            e.target.value = e.target.value.slice(0, 5);
                                                                        }
                                                                    }}
                                                                    error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 300 || measurementData.value <= 15)))}
                                                                    helperText={(measurementData.value && (measurementData.value >= 300 || measurementData.value <= 15)) ? "Thighs Size must be in between 15 to 300" : ''}
                                                                /> : measurementData?.type === 'armsSize' ?
                                                                    <TextField
                                                                        autoFocus
                                                                        required
                                                                        margin="dense"
                                                                        id="armsSize"
                                                                        name="armsSize"
                                                                        label="Arms Size(In Cms)"
                                                                        type="number"
                                                                        fullWidth
                                                                        variant="standard"
                                                                        value={measurementData.value}
                                                                        onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                                        onInput={(e) => {
                                                                            if (e.target.value.length > 5) {
                                                                                e.target.value = e.target.value.slice(0, 5);
                                                                            }
                                                                        }}
                                                                        error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 300 || measurementData.value <= 5)))}
                                                                        helperText={(measurementData.value && (measurementData.value >= 300 || measurementData.value <= 5)) ? "Arms Size must be in between 5 to 300" : ''}
                                                                    />
                                                                    : measurementData?.type === 'overallBodyFat' ?
                                                                        <TextField
                                                                            autoFocus
                                                                            required
                                                                            margin="dense"
                                                                            id="overallBodyFat"
                                                                            name="overallBodyFat"
                                                                            label="Overall Body Fat Size(In %)"
                                                                            type="number"
                                                                            fullWidth
                                                                            variant="standard"
                                                                            value={measurementData.value}
                                                                            onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                                            onInput={(e) => {
                                                                                if (e.target.value.length > 5) {
                                                                                    e.target.value = e.target.value.slice(0, 5);
                                                                                }
                                                                            }}
                                                                            error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 100 || measurementData.value <= 0)))}
                                                                            helperText={(measurementData.value && (measurementData.value >= 100 || measurementData.value <= 0)) ? "Overall Body Fat must be in between 0 to 100" : ''}
                                                                        /> : measurementData?.type === 'skeletalMuscleFat' ?
                                                                            <TextField
                                                                                autoFocus
                                                                                required
                                                                                margin="dense"
                                                                                id="skeletalMuscleFat"
                                                                                name="skeletalMuscleFat"
                                                                                label="Skeletal Muscle Fat Size(In %)"
                                                                                type="number"
                                                                                fullWidth
                                                                                variant="standard"
                                                                                value={measurementData.value}
                                                                                onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                                                onInput={(e) => {
                                                                                    if (e.target.value.length > 5) {
                                                                                        e.target.value = e.target.value.slice(0, 5);
                                                                                    }
                                                                                }}
                                                                                error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 100 || measurementData.value <= 0)))}
                                                                                helperText={(measurementData.value && (measurementData.value >= 100 || measurementData.value <= 0)) ? "Skeletal Muscle must be in between 0 to 100" : ''}
                                                                            /> : measurementData?.type === 'visceralFat' ?
                                                                                <TextField
                                                                                    autoFocus
                                                                                    required
                                                                                    margin="dense"
                                                                                    id="visceralFat"
                                                                                    name="visceralFat"
                                                                                    label="Visceral Fat Size(In %)"
                                                                                    type="number"
                                                                                    fullWidth
                                                                                    variant="standard"
                                                                                    value={measurementData.value}
                                                                                    onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                                                    onInput={(e) => {
                                                                                        if (e.target.value.length > 5) {
                                                                                            e.target.value = e.target.value.slice(0, 5);
                                                                                        }
                                                                                    }}
                                                                                    error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 100 || measurementData.value <= 0)))}
                                                                                    helperText={(measurementData.value && (measurementData.value >= 100 || measurementData.value <= 0)) ? "Visceral Fat must be in between 0 to 100" : ''}
                                                                                /> : measurementData?.type === 'height' ?
                                                                                    <TextField
                                                                                        autoFocus
                                                                                        required
                                                                                        margin="dense"
                                                                                        id="height"
                                                                                        name="height"
                                                                                        label="Height(In Cms)"
                                                                                        type="number"
                                                                                        fullWidth
                                                                                        variant="standard"
                                                                                        value={measurementData.value}
                                                                                        onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                                                        onInput={(e) => {
                                                                                            if (e.target.value.length > 5) {
                                                                                                e.target.value = e.target.value.slice(0, 5);
                                                                                            }
                                                                                        }}
                                                                                        error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 300 || measurementData.value <= 120)))}
                                                                                        helperText={(measurementData.value && (measurementData.value >= 300 || measurementData.value <= 120)) ? "Height must be in between 120 to 300" : ''}
                                                                                    /> : measurementData?.type === 'subcutaneousFat' ?
                                                                                        <TextField
                                                                                            autoFocus
                                                                                            required
                                                                                            margin="dense"
                                                                                            id="subcutaneousFat"
                                                                                            name="subcutaneousFat"
                                                                                            label="Subcutaneous Fat(In %)"
                                                                                            type="number"
                                                                                            fullWidth
                                                                                            variant="standard"
                                                                                            value={measurementData.value}
                                                                                            onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                                                            onInput={(e) => {
                                                                                                if (e.target.value.length > 5) {
                                                                                                    e.target.value = e.target.value.slice(0, 5);
                                                                                                }
                                                                                            }}
                                                                                            error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 100 || measurementData.value <= 0)))}
                                                                                            helperText={(measurementData.value && (measurementData.value >= 100 || measurementData.value <= 0)) ? "Subcutaneous Fat must be in between 0 to 100" : ''}
                                                                                        /> : measurementData?.type === 'bmr' ?
                                                                                            <TextField
                                                                                                autoFocus
                                                                                                required
                                                                                                margin="dense"
                                                                                                id="bmr"
                                                                                                name="bmr"
                                                                                                label="Basal Metabolic Rate (BMR)(In Cal/day)"
                                                                                                type="number"
                                                                                                fullWidth
                                                                                                variant="standard"
                                                                                                value={measurementData.value}
                                                                                                onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                                                                onInput={(e) => {
                                                                                                    if (e.target.value.length > 5) {
                                                                                                        e.target.value = e.target.value.slice(0, 5);
                                                                                                    }
                                                                                                }}
                                                                                                error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 5000 || measurementData.value <= 500)))}
                                                                                                helperText={(measurementData.value && (measurementData.value >= 5000 || measurementData.value <= 500)) ? "Basal Metabolic Rate (BMR) must be in between 500 to 5000" : ''}
                                                                                            /> : measurementData?.type === 'neck' ?
                                                                                                <TextField
                                                                                                    autoFocus
                                                                                                    required
                                                                                                    margin="dense"
                                                                                                    id="neck"
                                                                                                    name="neck"
                                                                                                    label="Neck Size(In Cms)"
                                                                                                    type="number"
                                                                                                    fullWidth
                                                                                                    variant="standard"
                                                                                                    value={measurementData.value}
                                                                                                    onChange={(e) => setMeasurementData({ ...measurementData, value: e.target.value })}
                                                                                                    onInput={(e) => {
                                                                                                        if (e.target.value.length > 5) {
                                                                                                            e.target.value = e.target.value.slice(0, 5);
                                                                                                        }
                                                                                                    }}
                                                                                                    error={(measurementData.value && (!weightRegex.test(measurementData.value) || (measurementData.value >= 300 || measurementData.value <= 5)))}
                                                                                                    helperText={(measurementData.value && (measurementData.value >= 300 || measurementData.value <= 5)) ? "Neck Size must be in between 5 to 300" : ''}
                                                                                                /> : ''
                                            }
                                        </Box>

                                        <Textarea
                                            rows="2"
                                            value={measurementData.userComment}
                                            className="w-full p-2 rounded"
                                            placeholder="Update comment... (optional)"
                                            onChange={(e) =>
                                                setMeasurementData((prev) => ({ ...prev, userComment: e.target.value }))
                                            }
                                        />
                                        <Box item display="flex" justifyContent="flex-end" gap={2} marginTop={2}>
                                            <IconButton size='small' onClick={() => setOpenDelete(true)}>   <DeleteIcon color='error' /></IconButton>
                                            <StyledButton
                                                variant="contained"
                                                color="primary"
                                                disabled={!measurementData?.value && !measurementData?.recordedAt ? true : false}
                                                onClick={handleSaveEdit}
                                            >
                                                Update
                                            </StyledButton>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    ))
                )}
            </DialogContent>
            <DeleteDialog open={openDelete} setOpen={setOpenDelete} onSubmit={onDeletemeasurement} name="Record" />
        </Dialog>
    );

}






